import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker, useRouter } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  PoReceiveInput,
  QuickReceiveInput,
  AutoPriceSummary,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import {
  isRetryableMutationRefusal,
  refusalReason,
  refusalStatus,
} from '../../lib/refusal';
import { useSession } from '../session';

type PendingPopGuard = (event: PopStateEvent) => void;
const pendingPopGuards = new WeakMap<Window, Set<PendingPopGuard>>();

/** Install before createRouter: Window-target events preserve registration order,
 * even when a later listener uses capture. Hooks register with this early listener. */
export function installPendingWriteNavigationGuard(
  target: Window | undefined = typeof window === 'undefined' ? undefined : window,
) {
  if (!target || pendingPopGuards.has(target)) return;
  const guards = new Set<PendingPopGuard>();
  pendingPopGuards.set(target, guards);
  target.addEventListener(
    'popstate',
    (event) => {
      for (const guard of guards) {
        guard(event);
        if (event.cancelBubble) return;
      }
    },
    { capture: true },
  );
}

/** Keep the pending form mounted while native history returns to its exact entry.
 * Pinned history 1.162.0 restores every blocked POP with go(1), which fails for
 * Forward and multi-entry traversal. Dispatch pending POPs before its handler;
 * ordinary links and unload still use the router's public blocker below. */
export function usePendingWriteNavigation(
  hasPending: () => boolean,
  shouldBlock: () => boolean,
) {
  const router = useRouter(),
    admittedPop = useRef(false);
  useEffect(() => {
    const target = window;
    installPendingWriteNavigationGuard(target);
    const guards = pendingPopGuards.get(target)!;
    let restoring: number | undefined,
      admissionTimer: ReturnType<typeof setTimeout> | undefined;
    const onPop = (event: PopStateEvent) => {
      const next = event.state?.__TSR_index;
      const current = router?.history.location.state.__TSR_index;
      if (restoring !== undefined) {
        event.stopImmediatePropagation();
        if (next === restoring || !Number.isInteger(next)) restoring = undefined;
        else if (Number.isInteger(next)) target.history.go(restoring - next);
        return;
      }
      if (
        !hasPending() ||
        !Number.isInteger(next) ||
        !Number.isInteger(current) ||
        next === current
      )
        return;
      if (!shouldBlock()) {
        admittedPop.current = true;
        clearTimeout(admissionTimer);
        admissionTimer = setTimeout(() => {
          admittedPop.current = false;
        }, 0);
        return;
      }
      event.stopImmediatePropagation();
      restoring = current;
      target.history.go(current! - next);
    };
    guards.add(onPop);
    return () => {
      clearTimeout(admissionTimer);
      admittedPop.current = false;
      guards.delete(onPop);
    };
  }, [router, hasPending, shouldBlock]);
  const routerShouldBlock = useCallback(() => {
    if (admittedPop.current) {
      admittedPop.current = false;
      return false;
    }
    return shouldBlock();
  }, [shouldBlock]);
  useBlocker({ shouldBlockFn: routerShouldBlock, enableBeforeUnload: hasPending });
}

export type ReceiptPriceLocator =
  | { availability: 'available'; operationId: string | null }
  | { availability: 'unavailable' };
type Completion = {
  owner: string;
  generation: number;
  purchaseOrderId: string;
  locator: ReceiptPriceLocator;
};
let completion: Completion | undefined;
let completionOwner: string | undefined;
function completionAccount(owner: string) {
  if (completionOwner !== owner) {
    completion = undefined;
    completionOwner = owner;
  }
}
export function useReceiptCompletion(purchaseOrderId: string) {
  const s = useSession(),
    owner = `${s.orgId}:${s.userId}:${s.role}`;
  completionAccount(owner);
  return completion?.owner === owner && completion.purchaseOrderId === purchaseOrderId
    ? completion.locator
    : undefined;
}
function rememberReceipt(owner: string, generation: number, answer: ReceiptAnswer) {
  completionAccount(owner);
  const summary = answer.autoPriceSummary;
  completion = {
    owner,
    generation,
    purchaseOrderId: answer.purchaseOrder.id,
    locator:
      summary?.availability === 'available'
        ? { availability: 'available', operationId: summary.operationId }
        : { availability: 'unavailable' },
  };
}
type Intent =
  | { action: 'receive'; input: Omit<PoReceiveInput, 'clientId' | 'mutationId'> }
  | { action: 'counter'; input: Omit<QuickReceiveInput, 'clientId' | 'mutationId'> };
type Command =
  | { action: 'receive'; input: PoReceiveInput }
  | { action: 'counter'; input: QuickReceiveInput };
export type ReceiptAnswer = {
  autoPriceSummary?: AutoPriceSummary;
  purchaseOrder: { id: string };
  overReceived?: { name: string; byHundredths: number }[];
};
type Attempt = Command & {
  phase: 'writing' | 'retry' | 'review' | 'reconcile' | 'refreshing' | 'read_failed';
  accepted: boolean;
  uncertain: boolean;
  running: boolean;
  error?: string;
  answer?: ReceiptAnswer;
};
/** Two receipt actions only. The owner stays above account-keyed forms and dismissed sheets.
 * A lost answer keeps the original nested intent; an accepted answer permits reads only. */
export function usePoReceiptWrite(
  onComplete: (answer: ReceiptAnswer) => void,
  surface = 'counter',
) {
  const session = useSession(),
    accountOwner = `${session.orgId}:${session.userId}:${session.role}`,
    owner = `${accountOwner}:${surface}`;
  completionAccount(accountOwner);
  const current = useRef(owner),
    generation = useRef(0),
    alive = useRef(true),
    completed = useRef(onComplete);
  completed.current = onComplete;
  if (current.current !== owner) {
    current.current = owner;
    generation.current++;
  }
  const records = useRef(new Map<string, Attempt>()),
    keys = useRef(createIntentKey()),
    [, tick] = useState(0);
  const qc = useQueryClient(),
    trpc = useTRPC();
  const receive = useMutation(
    trpc.purchasing.receive.mutationOptions({ networkMode: 'always', retry: false }),
  );
  const counter = useMutation(
    trpc.purchasing.quickReceive.mutationOptions({ networkMode: 'always', retry: false }),
  );
  const notify = () => {
    if (alive.current && current.current === owner) tick((v) => v + 1);
  };
  const attempt = records.current.get(owner),
    foreignPending = !attempt && records.current.size > 0;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  // Router history covers Back/Forward and programmatic navigation as well as links.
  // Read the registry at navigation time: completion clears it before its callback navigates.
  const hasPending = useCallback(() => records.current.size > 0, []);
  const shouldBlock = useCallback(
    () =>
      hasPending() &&
      !window.confirm(
        'This receipt is unresolved. Leaving ends this session-only recovery. Leave anyway?',
      ),
    [hasPending],
  );
  usePendingWriteNavigation(hasPending, shouldBlock);
  const refresh = async (review = false) => {
    const a = records.current.get(owner);
    if (
      !a ||
      a.running ||
      current.current !== owner ||
      !alive.current ||
      (review ? a.phase !== 'review' : !a.accepted)
    )
      return false;
    a.running = true;
    a.phase = 'refreshing';
    notify();
    const gen = generation.current;
    const owns = () =>
      alive.current && current.current === owner && generation.current === gen;
    try {
      const poId =
        a.answer?.purchaseOrder.id ??
        (a.action === 'receive' ? a.input.purchaseOrderId : undefined);
      if (poId) {
        const order = trpc.purchasing.get.queryOptions({ purchaseOrderId: poId });
        const evidence = trpc.pricing.receiptEvidence.queryOptions({
          purchaseOrderId: poId,
          offset: 0,
        });
        await Promise.all([
          qc.cancelQueries({ queryKey: order.queryKey }),
          qc.cancelQueries({ queryKey: evidence.queryKey }),
        ]);
        if (!owns()) {
          a.phase = a.accepted ? 'read_failed' : 'review';
          return false;
        }
        const [saved] = await Promise.all([
          qc.fetchQuery({ ...order, staleTime: 0, retry: false }),
          qc.fetchQuery({ ...evidence, staleTime: 0, retry: false }),
        ]);
        if (!saved || saved.po.id !== poId)
          throw Error(
            'The saved receipt needs office reconciliation. Its accepted result is retained.',
          );
      } else {
        await Promise.all([
          qc.fetchQuery({
            ...trpc.purchasing.vendors.queryOptions(),
            staleTime: 0,
            retry: false,
          }),
          qc.fetchQuery({
            ...trpc.inventory.locations.queryOptions(),
            staleTime: 0,
            retry: false,
          }),
        ]);
      }
      if (!owns()) {
        a.phase = a.accepted ? 'read_failed' : 'review';
        return false;
      }
      if (session.role === 'owner' || session.role === 'office') {
        await Promise.all([
          qc.fetchQuery({
            ...trpc.pricing.current.queryOptions({}),
            staleTime: 0,
            retry: false,
          }),
          qc.fetchQuery({
            ...trpc.pricing.raisedCount.queryOptions(),
            staleTime: 0,
            retry: false,
          }),
          ...(a.answer?.autoPriceSummary?.availability === 'available' &&
          a.answer.autoPriceSummary.operationId
            ? [
                qc.fetchQuery({
                  ...trpc.pricing.history.queryOptions({
                    operationId: a.answer.autoPriceSummary.operationId,
                    offset: 0,
                  }),
                  staleTime: 0,
                  retry: false,
                }),
              ]
            : []),
        ]);
        if (!owns()) return false;
        await qc.invalidateQueries(
          { queryKey: trpc.pricing.pathKey() },
          { throwOnError: true },
        );
        if (!owns()) return false;
      }
      const projections = [
        trpc.inventory.onHand.pathKey(),
        trpc.purchasing.list.pathKey(),
        trpc.purchasing.onOrder.pathKey(),
        trpc.restocks.reorderPlan.pathKey(),
      ];
      await Promise.all(projections.map((queryKey) => qc.cancelQueries({ queryKey })));
      if (!owns()) {
        a.phase = a.accepted ? 'read_failed' : 'review';
        return false;
      }
      await Promise.all(
        projections.map((queryKey) =>
          qc.invalidateQueries({ queryKey }, { throwOnError: true }),
        ),
      );
      if (!owns()) {
        a.phase = a.accepted ? 'read_failed' : 'review';
        return false;
      }
      if (!review && a.answer) rememberReceipt(accountOwner, gen, a.answer);
      records.current.delete(owner);
      keys.current.clear();
      notify();
      if (!review && a.answer) completed.current(a.answer);
      return true;
    } catch (error) {
      a.phase = a.accepted ? 'read_failed' : 'review';
      a.error = refusalReason(error);
      notify();
      return false;
    } finally {
      a.running = false;
      notify();
    }
  };
  const send = async () => {
    const a = records.current.get(owner);
    if (!a || a.running || a.accepted || current.current !== owner || !alive.current)
      return;
    a.running = true;
    a.phase = 'writing';
    notify();
    const gen = generation.current;
    try {
      a.answer =
        a.action === 'receive'
          ? await receive.mutateAsync(a.input)
          : await counter.mutateAsync(a.input);
      a.accepted = true;
      a.phase = 'read_failed';
    } catch (error) {
      const status = refusalStatus(error),
        reason = refusalReason(error),
        reconciliation =
          /office reconciliation|reconcile.*receipt|receipt.*reconcile/i.test(reason);
      if (status === 0 || status === 408 || status >= 500 || reconciliation)
        a.uncertain = true;
      a.phase = reconciliation
        ? 'reconcile'
        : isRetryableMutationRefusal(error)
          ? 'retry'
          : a.uncertain
            ? 'reconcile'
            : 'review';
      a.error =
        a.phase === 'reconcile'
          ? `Ask for office reconciliation before starting another receipt. ${reason}`
          : reason;
      notify();
      return;
    } finally {
      a.running = false;
      notify();
    }
    if (alive.current && current.current === owner && generation.current === gen)
      await refresh();
  };
  const begin = (intent: Intent) => {
    if (
      records.current.has(owner) ||
      foreignPending ||
      !alive.current ||
      current.current !== owner
    )
      return;
    const copied = {
      ...intent.input,
      lines: intent.input.lines.map((line) => Object.freeze({ ...line })),
    };
    Object.freeze(copied.lines);
    const input = Object.freeze({
      ...copied,
      ...keys.current.forIntent([owner, intent.action, copied]),
    });
    records.current.set(owner, {
      action: intent.action,
      input,
      phase: 'writing',
      accepted: false,
      uncertain: false,
      running: false,
    } as Attempt);
    notify();
    void send();
  };
  return {
    owner,
    attempt,
    foreignPending,
    locked: !!attempt || foreignPending,
    begin,
    retry: () => void send(),
    refresh: () => refresh(),
    review: () => refresh(true),
  };
}
export type PoReceiptWrite = ReturnType<typeof usePoReceiptWrite>;
